import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", keywords: [], ean_prefixes: [], list_ids: [] };
const join = (a) => (a || []).join(", ");
const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export function CategoryEditor({ cfg, saveCfg }) {
  const [edit, setEdit] = useState(null);
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/products/lists").then((r) => setLists(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const cat = { ...edit, keywords: split(edit.kw ?? join(edit.keywords)), ean_prefixes: split(edit.pf ?? join(edit.ean_prefixes)) };
      delete cat.kw; delete cat.pf;
      const cats = cat.id ? cfg.categories.map((c) => (c.id === cat.id ? cat : c)) : [...cfg.categories, cat];
      await saveCfg({ ...cfg, categories: cats });
      toast.success("Categoria salvata"); setEdit(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare la categoria?")) return;
    try { await saveCfg({ ...cfg, categories: cfg.categories.filter((c) => c.id !== id) }); } catch (e) { /* toast già mostrato */ }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="category-editor">
      {cfg.categories.map((c) => (
        <span key={c.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs" data-testid={`category-chip-${c.id}`}>
          {c.name}
          <button type="button" className="text-slate-400 hover:text-blue-600" data-testid={`category-edit-${c.id}`} onClick={() => setEdit(c)}><Pencil className="h-3 w-3" /></button>
          <button type="button" className="text-slate-400 hover:text-rose-600" data-testid={`category-delete-${c.id}`} onClick={() => remove(c.id)}><Trash2 className="h-3 w-3" /></button>
        </span>))}
      <Button size="sm" variant="outline" data-testid="category-add-button" onClick={() => setEdit({ ...EMPTY })}><Plus className="mr-1 h-3.5 w-3.5" />Nuova categoria</Button>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica categoria" : "Nuova categoria / reparto"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Nome</Label><Input data-testid="category-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="es. Norcineria" /></div>
              <div className="space-y-1"><Label>Parole chiave nella descrizione prodotto (separate da virgola)</Label><Input data-testid="category-keywords-input" value={edit.kw ?? join(edit.keywords)} onChange={(e) => setEdit({ ...edit, kw: e.target.value })} placeholder="NORCINERIA, SALUMI, PROSCIUTTO" /></div>
              <div className="space-y-1"><Label>Prefissi EAN / codice (separati da virgola)</Label><Input data-testid="category-prefixes-input" className="font-mono" value={edit.pf ?? join(edit.ean_prefixes)} onChange={(e) => setEdit({ ...edit, pf: e.target.value })} placeholder="2111, 2050" /></div>
              {lists.length > 0 && (
                <div className="space-y-1"><Label>Liste prodotti (Analisi prodotti)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {lists.map((l) => { const on = edit.list_ids.includes(l.id); return (
                      <button type="button" key={l.id} data-testid={`category-list-${l.id}`} onClick={() => setEdit({ ...edit, list_ids: on ? edit.list_ids.filter((x) => x !== l.id) : [...edit.list_ids, l.id] })}
                        className={`rounded-full border px-2.5 py-1 text-xs ${on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600 hover:border-blue-400"}`}>{l.name} ({l.items.length})</button>); })}
                  </div>
                </div>)}
              <p className="text-xs text-slate-500">Un prodotto appartiene alla categoria se soddisfa almeno uno dei criteri.</p>
            </div>)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button data-testid="category-save" className="bg-blue-600 hover:bg-blue-700" disabled={busy || !edit?.name?.trim()} onClick={save}>{busy ? "Salvo..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
